import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Layers, Image, PlaySquare, Radio,
  Clock, CheckCircle, XCircle, Upload, Youtube,
  Cpu, Calendar, FileText, Settings, Check,
  AlertCircle, ChevronDown, ChevronRight, BellOff, Power, CalendarClock, CalendarCheck,
} from 'lucide-react';
import { facilitiesApi, assetsApi, overlaysApi, dashboardApi, schedulesApi, queryKeys, type FacilityStatusSummary } from '@/lib/api';
import OnboardingOverview from '@/components/OnboardingOverview';
import { fmtInTz, timeRemaining, toZonedInputValue, zonedInputToInstant } from '@/lib/utils';
import { APP_VERSION } from '@/version';

// A far-future sentinel meaning "paused indefinitely".
const INDEFINITE_ISO = '2999-01-01T00:00:00.000Z';

function alertsPauseState(iso: string | null): { paused: boolean; label: string } {
  if (!iso) return { paused: false, label: '' };
  const d = new Date(iso);
  const t = d.getTime();
  if (isNaN(t) || t <= Date.now()) return { paused: false, label: '' }; // expired = not paused
  const indefinite = d.getUTCFullYear() >= 2999;
  return { paused: true, label: indefinite ? 'indefinitely' : `until ${d.toLocaleString()}` };
}

// Broadcast Mode: admin-only, auto-expiring engine kill switch. Active = a future
// timestamp. Labelled in the FACILITY's timezone (it ends at a time at the rink,
// not in whatever zone the admin happens to be sitting in) plus how long is left,
// so nobody has to work out from a bare timestamp whether it's about to lapse.
function broadcastModeState(iso: string | null, tz?: string): { active: boolean; label: string; remaining: string } {
  if (!iso) return { active: false, label: '', remaining: '' };
  const d = new Date(iso);
  const t = d.getTime();
  if (isNaN(t) || t <= Date.now()) return { active: false, label: '', remaining: '' }; // expired = off
  return { active: true, label: `until ${fmtInTz(iso, tz)}`, remaining: timeRemaining(iso) };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// A facility "needs attention" when something is actively broken or live and
// therefore worth surfacing first. Healthy idle facilities are hidden by default.
function needsAttention(f: FacilityStatusSummary): boolean {
  if (f.activeStreamCount > 0) return true;             // live stream — worth watching
  if (f.agentHostCount > 0 && !f.agentOnline) return true; // configured agent that's offline
  if (f.publishedCount > 0) return true;                // an overlay is live
  return false;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon, label, value, sub, color, to,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
  /** When set the whole tile becomes a link to this route. */
  to?: string;
}) {
  const body = (
    <div className={`h-full rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900${
      to ? ' transition-colors hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-800/60' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{sub}</p>}
        </div>
        <span className={`rounded-lg p-2.5 ${color}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

// ─── Schedule source badge ─────────────────────────────────────────────────────

function SourceBadge({ source }: { source: FacilityStatusSummary['scheduleSource'] }) {
  if (source === 'ccm_api') {
    return (
      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
        CCM
      </span>
    );
  }
  if (source === 'google_sheets') {
    return (
      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
        Sheets
      </span>
    );
  }
  if (source === 'leagueapps_api') {
    return (
      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
        LeagueApps
      </span>
    );
  }
  if (source === 'generic_recurring') {
    return (
      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
        Recurring
      </span>
    );
  }
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
      Manual
    </span>
  );
}

// ─── Compact facility row (collapsed view) ────────────────────────────────────

function FacilityRow({
  f, expanded, onToggle,
}: {
  f: FacilityStatusSummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();
  const pauseMut = useMutation({
    mutationFn: (pausedUntil: string | null) => dashboardApi.setAlertsPause(f.facilityId, pausedUntil),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-dashboard-status'] }),
  });
  const alertsPaused = alertsPauseState(f.alertsPausedUntil);

  const broadcastMode = broadcastModeState(f.broadcastModeUntil, f.timezone);
  const [bmEditing, setBmEditing] = useState(false);
  const [bmValue, setBmValue] = useState('');
  // The input is rink time: seeded from the stored instant in the facility's zone
  // and read back the same way, so what the admin types is what the site gets.
  const openBmEditor = () => { setBmValue(toZonedInputValue(f.broadcastModeUntil, f.timezone)); setBmEditing(true); };
  const bmInstant = zonedInputToInstant(bmValue, f.timezone);
  const bmFuture = !!bmInstant && bmInstant.getTime() > Date.now();
  const broadcastMut = useMutation({
    mutationFn: (until: string | null) => dashboardApi.setBroadcastMode(f.facilityId, until),
    onSuccess: () => {
      setBmEditing(false);
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard-status'] });
    },
  });

  // Status dot color: red (live), amber (offline agent / paused sheets), gray (inactive), green (healthy)
  let dotColor = 'bg-gray-300 dark:bg-gray-600';
  if (!f.isActive) dotColor = 'bg-gray-300 dark:bg-gray-600';
  else if (f.activeStreamCount > 0) dotColor = 'bg-red-500 animate-pulse';
  else if (f.agentHostCount > 0 && !f.agentOnline) dotColor = 'bg-amber-400';
  else if (f.agentHostCount > 0 && f.agentOnline) dotColor = 'bg-green-500';

  return (
    <div className={`rounded-lg border transition-colors ${
      f.activeStreamCount > 0
        ? 'border-red-300 bg-red-50/30 dark:border-red-800 dark:bg-red-950/10'
        : !f.isActive
          ? 'border-gray-100 bg-gray-50/40 opacity-60 dark:border-gray-800 dark:bg-gray-900/40'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
    }`}>
      {/* Collapsed row */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotColor}`} />

        <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
          {f.name}
        </span>

        {/* Compact status badges */}
        <div className="flex shrink-0 items-center gap-2 text-xs">
          {broadcastMode.active && (
            <span className="flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 font-bold tracking-wide text-white" title={`Broadcast Mode ${broadcastMode.label}`}>
              ⏻ BROADCAST
            </span>
          )}
          {f.activeStreamCount > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-600 dark:bg-red-900/40 dark:text-red-400">
              <Radio className="h-3 w-3" />
              {f.activeStreamCount}
            </span>
          )}
          {f.publishedCount > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <Layers className="h-3 w-3" />
              {f.publishedCount}
            </span>
          )}
          {f.upcomingScheduleCount > 0 && (
            <span className="hidden items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 sm:flex">
              <Calendar className="h-3 w-3" />
              {f.upcomingScheduleCount}
            </span>
          )}
          <span className="hidden items-center gap-1.5 sm:flex" title={f.lastSheetsSync ? `Last sync: ${new Date(f.lastSheetsSync).toLocaleString()}` : 'Manual schedule — no automatic refresh'}>
            <SourceBadge source={f.scheduleSource} />
            {f.lastSheetsSync && (
              <span className="hidden text-[11px] text-gray-400 dark:text-gray-500 md:inline">
                {timeAgo(f.lastSheetsSync)}
              </span>
            )}
          </span>
          {f.agentHostCount > 0 && (
            f.agentOnline ? (
              <span className="hidden items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-green-700 dark:bg-green-900/30 dark:text-green-300 sm:flex">
                <Cpu className="h-3 w-3" />
                online
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                <AlertCircle className="h-3 w-3" />
                offline
              </span>
            )
          )}
          {f.agentVersion && f.latestAgentVersion && f.agentVersion !== f.latestAgentVersion && (
            <span
              className="hidden items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 md:flex"
              title={`Update pending: v${f.agentVersion} → v${f.latestAgentVersion}`}
            >
              ↑ v{f.latestAgentVersion}
            </span>
          )}
          {!f.youtubeConnected && (
            <span className="hidden items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-gray-500 dark:bg-gray-800 dark:text-gray-400 md:flex">
              <Youtube className="h-3 w-3" />
              not connected
            </span>
          )}
          {alertsPaused.paused && (
            <span className="flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" title={`Alerts muted ${alertsPaused.label}`}>
              <BellOff className="h-3 w-3" />
              alerts off
            </span>
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">

            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400">
                {f.sheetCount} sheet{f.sheetCount !== 1 ? 's' : ''}
                {f.pausedSheetCount > 0 && <span className="ml-1 text-amber-600 dark:text-amber-400">({f.pausedSheetCount} suspended)</span>}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-gray-400" />
              <SourceBadge source={f.scheduleSource} />
              {f.lastSheetsSync && (
                <span className="text-xs text-gray-400">synced {timeAgo(f.lastSheetsSync)}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Layers className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400">
                {f.compositionCount} overlay{f.compositionCount !== 1 ? 's' : ''}
                {f.publishedCount > 0 && <span className="ml-1 text-red-600 dark:text-red-400">· {f.publishedCount} live</span>}
              </span>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5"><PlaySquare className="h-3.5 w-3.5 text-gray-400" />{f.slideshowCount} slideshow{f.slideshowCount !== 1 ? 's' : ''}</span>
              <span className="flex items-center gap-1.5"><Image className="h-3.5 w-3.5 text-gray-400" />{f.logoCount} logo{f.logoCount !== 1 ? 's' : ''}</span>
            </div>

            <div className="flex items-center gap-2">
              <Youtube className="h-3.5 w-3.5 text-gray-400" />
              {f.youtubeConnected ? (
                <span className="text-gray-700 dark:text-gray-300 truncate">{f.youtubeChannelName || 'Connected'}</span>
              ) : (
                <span className="text-gray-400">Not connected</span>
              )}
              {f.titleTemplate && <FileText className="h-3 w-3 text-gray-300" />}
            </div>

            <div className="flex items-center gap-2">
              <Cpu className="h-3.5 w-3.5 text-gray-400" />
              {f.agentHostCount === 0 ? (
                <span className="text-xs text-gray-400">No agent configured</span>
              ) : f.agentOnline ? (
                <>
                  <span className="text-xs text-green-600 dark:text-green-400">Online</span>
                  {f.agentLastSeen && <span className="text-xs text-gray-400">· last {timeAgo(f.agentLastSeen)}</span>}
                </>
              ) : (
                <>
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Offline</span>
                  {f.agentLastSeen && <span className="text-xs text-gray-400">· last seen {timeAgo(f.agentLastSeen)}</span>}
                </>
              )}
            </div>

            {/* Agent version (with update-available badge) */}
            {f.agentVersion && (
              <div className="flex items-center gap-2">
                <Cpu className="h-3.5 w-3.5 text-gray-300" />
                {(() => {
                  const updateAvailable = f.latestAgentVersion && f.agentVersion !== f.latestAgentVersion;
                  return (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        updateAvailable
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                      title={updateAvailable
                        ? `Server has v${f.latestAgentVersion}; will auto-update at next safe window`
                        : 'Running latest version'}
                    >
                      v{f.agentVersion}
                      {updateAvailable && <span className="ml-1">↑ v{f.latestAgentVersion}</span>}
                    </span>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Alert mute control */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
            <span className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              <BellOff className="h-3.5 w-3.5" /> Alerts:
            </span>
            {alertsPaused.paused ? (
              <>
                <span className="text-xs font-medium text-indigo-600 dark:text-indigo-300">Muted {alertsPaused.label}</span>
                <button disabled={pauseMut.isPending} onClick={() => pauseMut.mutate(null)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  Resume alerts
                </button>
              </>
            ) : (
              <>
                <span className="text-xs text-gray-400">Pause for</span>
                {[{ l: '24h', h: 24 }, { l: '3 days', h: 72 }, { l: '7 days', h: 168 }].map((o) => (
                  <button key={o.l} disabled={pauseMut.isPending}
                    onClick={() => pauseMut.mutate(new Date(Date.now() + o.h * 3600 * 1000).toISOString())}
                    className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">{o.l}</button>
                ))}
                <button disabled={pauseMut.isPending} onClick={() => pauseMut.mutate(INDEFINITE_ISO)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">Indefinitely</button>
                <input type="date" title="Mute until end of the chosen day"
                  onChange={(e) => { if (e.target.value) pauseMut.mutate(new Date(`${e.target.value}T23:59:59`).toISOString()); }}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
              </>
            )}
          </div>

          {/* Broadcast Mode control (admin-only engine kill switch) */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
            <span className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300" title="Disables the agent engine so you can run OBS/streaming manually. Auto-expires at the end time.">
              <Power className="h-3.5 w-3.5" /> Broadcast Mode:
            </span>
            {/* The editor is checked FIRST. It used to sit behind `broadcastMode.active`,
                which made "Change end" a dead button: it set bmEditing, the active
                branch re-rendered, and the input was never reachable. */}
            {bmEditing ? (
              <>
                <input type="datetime-local" value={bmValue} autoFocus
                  onChange={(e) => setBmValue(e.target.value)}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                <button disabled={broadcastMut.isPending || !bmFuture}
                  onClick={() => { if (bmInstant && bmFuture) broadcastMut.mutate(bmInstant.toISOString()); }}
                  className="rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                  {broadcastMode.active ? 'Save end time' : 'Enable'}
                </button>
                <button disabled={broadcastMut.isPending} onClick={() => setBmEditing(false)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  rink time ({f.timezone})
                </span>
                {/* Confirm the duration and, when the admin isn't in the facility's
                    zone, what the same moment is on their own clock. */}
                {bmValue && (
                  bmFuture ? (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      = {timeRemaining(bmInstant!.toISOString())}
                      {toZonedInputValue(bmInstant!.toISOString()) !== bmValue && (
                        <> · {bmInstant!.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} your time</>
                      )}
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-amber-600 dark:text-amber-500">
                      {bmInstant ? 'that time has already passed at the rink' : 'enter a date and time'}
                    </span>
                  )
                )}
                {broadcastMut.isError && (
                  <span className="text-[11px] font-medium text-red-600 dark:text-red-400">
                    Couldn't save — {(broadcastMut.error as Error)?.message ?? 'try again'}
                  </span>
                )}
              </>
            ) : broadcastMode.active ? (
              <>
                <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">ON {broadcastMode.label}</span>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300" title="Broadcast Mode ends automatically at the end time">
                  {broadcastMode.remaining}
                </span>
                <button disabled={broadcastMut.isPending} onClick={openBmEditor}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  Change end
                </button>
                <button disabled={broadcastMut.isPending} onClick={() => broadcastMut.mutate(null)}
                  className="rounded border border-red-300 px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30">
                  End now
                </button>
              </>
            ) : (
              <>
                <span className="text-xs text-gray-400">Off</span>
                <button disabled={broadcastMut.isPending} onClick={openBmEditor}
                  className="rounded border border-gray-300 px-2 py-0.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  Enable…
                </button>
              </>
            )}
          </div>

          <div className="mt-3 flex items-center gap-3 text-xs">
            <Link to={`/facilities/${f.id}/settings`} className="text-blue-600 hover:underline dark:text-blue-400">
              <Settings className="mr-1 inline h-3 w-3" />Settings
            </Link>
            <Link to={`/schedules?facility=${f.facilityId}`} className="text-blue-600 hover:underline dark:text-blue-400">
              <Calendar className="mr-1 inline h-3 w-3" />Schedules
            </Link>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-gray-400">{f.timezone}</span>
            {f.isActive ? (
              <span className="ml-auto flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircle className="h-3 w-3" /> Active
              </span>
            ) : (
              <span className="ml-auto flex items-center gap-1 text-gray-400">
                <XCircle className="h-3 w-3" /> Inactive
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type FilterMode = 'attention' | 'active' | 'all';

export default function AdminStatusTab() {
  const [filter, setFilter] = useState<FilterMode>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: statusData, isLoading: loadingStatus } = useQuery({
    queryKey: ['admin-dashboard-status'],
    queryFn: () => dashboardApi.facilityStatus(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Feeds the Live Now and Upcoming Streams tiles, and is the same payload
  // the pages they link to render.
  const { data: fleet } = useQuery({
    queryKey: queryKeys.schedules.fleetOverview(),
    queryFn: () => schedulesApi.getFleetOverview(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Feeds the "Sites With Games" tile; same payload as the page it links to.
  const { data: scheduleHealth } = useQuery({
    queryKey: queryKeys.schedules.scheduleHealth(),
    queryFn: () => schedulesApi.getScheduleHealth(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const { data: assetsData, isLoading: loadingAssets } = useQuery({
    queryKey: ['admin-status-assets'],
    queryFn: () => assetsApi.list({ limit: 10, isActive: true }),
    staleTime: 30000,
  });

  const { data: compositionsData, isLoading: loadingCompositions } = useQuery({
    queryKey: ['admin-status-compositions'],
    queryFn: () => overlaysApi.listCompositions(),
    staleTime: 30000,
  });

  const { data: facilitiesData } = useQuery({
    queryKey: ['admin-status-facilities'],
    queryFn: () => facilitiesApi.list({ limit: 100 }),
    staleTime: 30000,
  });

  // Deployed backend version (for at-a-glance deploy verification).
  const { data: backendInfo } = useQuery({
    queryKey: ['api-info'],
    queryFn: async () => {
      const r = await fetch('/api/info');
      return r.ok ? ((await r.json()) as { version?: string }) : null;
    },
    staleTime: 300000,
  });

  const isLoading = loadingStatus || loadingAssets || loadingCompositions;

  const summaries = (statusData?.facilities ?? []).sort((a, b) => {
    // Sort: live streams first, then needs-attention, then by name
    const aLive = a.activeStreamCount > 0 ? 0 : 1;
    const bLive = b.activeStreamCount > 0 ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    const aAttn = needsAttention(a) ? 0 : 1;
    const bAttn = needsAttention(b) ? 0 : 1;
    if (aAttn !== bAttn) return aAttn - bAttn;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  const recentAssets  = assetsData?.assets ?? [];
  const compositions  = compositionsData?.compositions ?? [];
  const facilities    = facilitiesData?.facilities ?? [];
  const facilityMap   = Object.fromEntries(facilities.map(f => [f.facilityId, f]));

  const activeFacilities  = summaries.filter(f => f.isActive).length;
  const totalPublished    = summaries.reduce((n, f) => n + f.publishedCount, 0);
  const totalSlideshows   = summaries.reduce((n, f) => n + f.slideshowCount, 0);
  const totalLogos        = summaries.reduce((n, f) => n + f.logoCount, 0);
  const agentsConfigured  = summaries.filter(f => f.agentHostCount > 0).length;
  const agentsOnline      = summaries.filter(f => f.agentHostCount > 0 && f.agentOnline).length;
  const totalActiveStreams = summaries.reduce((n, f) => n + f.activeStreamCount, 0);

  // Both tile counts come from /fleet-overview, the same source the linked
  // pages render, so a tile can never disagree with the page behind it.
  // Live Now still shows the facility-summary total until this resolves,
  // which keeps the tile populated on first paint.
  const liveSites = fleet?.counts.liveSites ?? 0;
  const upcomingToday = fleet?.counts.upcomingToday ?? 0;
  const upcomingSites = fleet?.counts.upcomingSites ?? 0;

  const attentionCount = summaries.filter(needsAttention).length;

  // Apply filter
  const visibleSummaries = summaries.filter(f => {
    if (filter === 'all') return true;
    if (filter === 'active') return f.isActive;
    return needsAttention(f);
  });

  const liveNow = compositions.filter(c => c.isPublished);

  const toggleRow = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-28 animate-pulse rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
          ))}
        </div>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 animate-pulse rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard
          icon={Building2}
          label="Active Facilities"
          value={activeFacilities}
          sub={`${summaries.length} total`}
          color="bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
        />
        <StatCard
          icon={Cpu}
          label="Agents Online"
          value={`${agentsOnline}/${agentsConfigured}`}
          sub={agentsConfigured === 0 ? 'none configured' : `${agentsConfigured - agentsOnline} offline`}
          color={agentsOnline === agentsConfigured && agentsConfigured > 0
            ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
          }
        />
        <StatCard
          icon={Radio}
          label="Live Now"
          value={totalActiveStreams}
          sub={totalActiveStreams === 0
            ? `no active streams · ${totalPublished} overlay${totalPublished !== 1 ? 's' : ''} published`
            : `across ${liveSites} site${liveSites !== 1 ? 's' : ''}`}
          color={totalActiveStreams > 0
            ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
            : 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }
          to="/streams/live"
        />
        <StatCard
          icon={CalendarClock}
          label="Upcoming Streams"
          value={upcomingToday}
          sub={upcomingToday === 0 ? 'none left today' : `today, across ${upcomingSites} site${upcomingSites !== 1 ? 's' : ''}`}
          color={upcomingToday > 0
            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
            : 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }
          to="/streams/upcoming"
        />
        <StatCard
          icon={PlaySquare}
          label="Slideshows"
          value={totalSlideshows}
          sub={`${totalLogos} logo${totalLogos !== 1 ? 's' : ''} · across all facilities`}
          color="bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
        />
        {/* Replaced the Logo Assets tile (2026-10-05): which sites actually have
            games coming. Problems first in the subtitle, then the quiet ones. */}
        <StatCard
          icon={CalendarCheck}
          label="Sites With Games"
          value={scheduleHealth ? `${scheduleHealth.counts.hasGames}/${scheduleHealth.counts.total}` : '—'}
          sub={!scheduleHealth ? 'next 7 days'
            : scheduleHealth.counts.syncProblem > 0
              ? `${scheduleHealth.counts.syncProblem} sync problem${scheduleHealth.counts.syncProblem !== 1 ? 's' : ''} · next 7 days`
              : `${scheduleHealth.counts.connectedEmpty} connected but empty · next 7 days`}
          color={scheduleHealth && scheduleHealth.counts.syncProblem > 0
            ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
            : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'}
          to="/streams/schedules"
        />
      </div>

      {/* ── Onboarding readiness (fleet-wide) ───────────────────────────────── */}
      <OnboardingOverview />

      {/* ── Facilities (compact rows with filter) ───────────────────────────── */}
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Facilities
              <span className="ml-2 text-xs font-normal text-gray-400">
                ({visibleSummaries.length} of {summaries.length})
              </span>
            </h2>
          </div>

          {/* Filter chips */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800/50">
            {([
              { id: 'attention', label: 'Needs Attention', count: attentionCount },
              { id: 'active',    label: 'Active',          count: activeFacilities },
              { id: 'all',       label: 'All',             count: summaries.length },
            ] as Array<{ id: FilterMode; label: string; count: number }>).map(opt => (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                className={`flex items-center gap-2 rounded px-3 py-1 text-xs font-medium transition-colors ${
                  filter === opt.id
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {opt.label}
                <span className={`rounded-full px-1.5 text-[10px] ${
                  filter === opt.id
                    ? 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {opt.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          {visibleSummaries.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400 dark:border-gray-700">
              {filter === 'attention'
                ? 'Nothing needs attention right now. All quiet.'
                : 'No facilities match this filter.'}
            </p>
          ) : (
            visibleSummaries.map(f => (
              <FacilityRow
                key={f.facilityId}
                f={f}
                expanded={expandedIds.has(f.facilityId)}
                onToggle={() => toggleRow(f.facilityId)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Live Now + Recent Uploads (less prominent, below facilities) ─── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* Live overlays detail */}
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <Radio className="h-4 w-4 text-red-500" />
            <h2 className="text-sm font-semibold">Live Overlays</h2>
            {liveNow.length > 0 && (
              <span className="ml-auto flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-900/30 dark:text-red-400">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                {liveNow.length} active
              </span>
            )}
          </div>
          <div className="max-h-64 divide-y divide-gray-50 overflow-auto dark:divide-gray-800">
            {liveNow.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">No overlays currently live</p>
            ) : (
              liveNow.map(c => {
                const scene = c.scenes?.find(s => s.id === c.activeSceneId);
                const facility = facilityMap[c.facilityId];
                return (
                  <div key={c.id} className="flex items-center justify-between px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{c.name}</p>
                      <p className="truncate text-xs text-gray-400">
                        {facility?.name ?? c.facilityId} · Sheet {c.sheetId}
                        {scene && <span className="ml-1 text-gray-500">· {scene.name}</span>}
                      </p>
                    </div>
                    <Link
                      to={`/overlays/${c.id}/edit`}
                      className="ml-3 shrink-0 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      Edit →
                    </Link>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Uploads */}
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <Upload className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold">Recent Uploads</h2>
          </div>
          <div className="max-h-64 divide-y divide-gray-50 overflow-auto dark:divide-gray-800">
            {recentAssets.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">No assets uploaded yet</p>
            ) : (
              recentAssets.slice(0, 8).map(a => (
                <div key={a.id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-900 dark:text-gray-100">{a.originalName}</p>
                    <p className="text-xs text-gray-400">{a.facility?.name ?? a.facilityId}</p>
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      {a.assetType}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="h-3 w-3" />
                      {timeAgo(a.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Build versions (deploy verification) ─────────────────────────────── */}
      <div className="pt-1 text-center text-[11px] text-gray-400 dark:text-gray-500">
        CSM · frontend v{APP_VERSION} · backend v{backendInfo?.version ?? '—'}
      </div>
    </div>
  );
}
